/**
 * FloatingShapes Component
 *
 * Animated geometric shapes floating in the background.
 * Features:
 * - Various shape types (circles, triangles, rectangles, hexagons)
 * - Slow drifting movement with rotation
 * - Blur effect for depth perception
 * - Semi-transparent appearance
 */

import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, AbsoluteFill } from 'remotion';
import { seededRandom } from '../utils';

export type ShapeType = 'circle' | 'triangle' | 'rectangle' | 'hexagon' | 'diamond';

export interface FloatingShape {
  type: ShapeType;
  x: number;          // Position X (0-100%)
  y: number;          // Position Y (0-100%)
  size: number;       // Size in pixels
  rotation?: number;  // Initial rotation in degrees
  blur?: number;      // Blur amount in pixels
  opacity?: number;   // Override opacity (0-1)
}

export interface FloatingShapesProps {
  /** Array of shape configurations */
  shapes?: FloatingShape[];
  /** Primary color for shapes */
  color?: string;
  /** Movement speed multiplier (0.1 = slow, 1 = normal) */
  speed?: number;
  /** Frame when shapes start appearing */
  startFrame?: number;
  /** Default opacity for shapes (0-1) */
  defaultOpacity?: number;
  /** Default blur amount */
  defaultBlur?: number;
  /** Auto-generate shapes if none provided */
  autoGenerate?: boolean;
  /** Number of shapes to auto-generate */
  autoCount?: number;
  /** Random seed for auto-generation */
  seed?: number;
}

/**
 * Shape renderers
 */
const ShapeRenderers: Record<ShapeType, (size: number, color: string) => React.ReactNode> = {
  circle: (size, color) => (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: color,
      }}
    />
  ),

  triangle: (size, color) => (
    <svg width={size} height={size} viewBox="0 0 100 100">
      <polygon
        points="50,10 90,90 10,90"
        fill={color}
      />
    </svg>
  ),

  rectangle: (size, color) => (
    <div
      style={{
        width: size,
        height: size * 0.6,
        borderRadius: size * 0.1,
        backgroundColor: color,
      }}
    />
  ),

  hexagon: (size, color) => (
    <svg width={size} height={size} viewBox="0 0 100 100">
      <polygon
        points="50,3 93,25 93,75 50,97 7,75 7,25"
        fill={color}
      />
    </svg>
  ),

  diamond: (size, color) => (
    <svg width={size} height={size} viewBox="0 0 100 100">
      <polygon
        points="50,5 95,50 50,95 5,50"
        fill={color}
      />
    </svg>
  ),
};

export const FloatingShapes: React.FC<FloatingShapesProps> = ({
  shapes: providedShapes,
  color = 'rgba(255, 255, 255, 0.1)',
  speed = 0.3,
  startFrame = 0,
  defaultOpacity = 0.15,
  defaultBlur = 4,
  autoGenerate = false,
  autoCount = 8,
  seed = 42424,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame;

  // Generate shapes if auto-generate is enabled and no shapes provided
  let shapes = providedShapes || [];

  if ((autoGenerate || shapes.length === 0) && !providedShapes) {
    const random = seededRandom(seed);
    const shapeTypes: ShapeType[] = ['circle', 'triangle', 'rectangle', 'hexagon', 'diamond'];

    shapes = Array.from({ length: autoCount }).map(() => ({
      type: shapeTypes[Math.floor(random() * shapeTypes.length)],
      x: random() * 100,
      y: random() * 100,
      size: 40 + random() * 80,
      rotation: random() * 360,
      blur: 2 + random() * 6,
      opacity: 0.08 + random() * 0.12,
    }));
  }

  // Don't render before start frame
  if (relativeFrame < 0) {
    return null;
  }

  // Entrance fade
  const entranceOpacity = interpolate(
    relativeFrame,
    [0, fps * 1],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );

  return (
    <AbsoluteFill style={{ pointerEvents: 'none', overflow: 'hidden' }}>
      {shapes.map((shape, index) => {
        // Unique movement for each shape
        const phaseX = index * 1.3;
        const phaseY = index * 0.7;
        const phaseRotation = index * 2.1;

        // Slow drifting motion
        const driftX = Math.sin((relativeFrame * speed * 0.02) + phaseX) * 20;
        const driftY = Math.sin((relativeFrame * speed * 0.015) + phaseY) * 15;
        const rotation = (shape.rotation || 0) + Math.sin((relativeFrame * speed * 0.01) + phaseRotation) * 15;

        const opacity = (shape.opacity ?? defaultOpacity) * entranceOpacity;
        const blur = shape.blur ?? defaultBlur;

        return (
          <div
            key={index}
            style={{
              position: 'absolute',
              left: `${shape.x}%`,
              top: `${shape.y}%`,
              transform: `translate(-50%, -50%) translate(${driftX}px, ${driftY}px) rotate(${rotation}deg)`,
              opacity,
              filter: `blur(${blur}px)`,
            }}
          >
            {ShapeRenderers[shape.type](shape.size, color)}
          </div>
        );
      })}
    </AbsoluteFill>
  );
};

/**
 * FloatingCircles - Preset with only circles (bokeh-like effect)
 */
export interface FloatingCirclesProps {
  /** Number of circles */
  count?: number;
  /** Circle color */
  color?: string;
  /** Movement speed */
  speed?: number;
  /** Start frame */
  startFrame?: number;
  /** Random seed */
  seed?: number;
}

export const FloatingCircles: React.FC<FloatingCirclesProps> = ({
  count = 10,
  color = 'rgba(255, 255, 255, 0.1)',
  speed = 0.3,
  startFrame = 0,
  seed = 11111,
}) => {
  const random = seededRandom(seed);

  const shapes: FloatingShape[] = Array.from({ length: count }).map(() => ({
    type: 'circle' as ShapeType,
    x: random() * 100,
    y: random() * 100,
    size: 30 + random() * 100,
    blur: 3 + random() * 8,
    opacity: 0.05 + random() * 0.1,
  }));

  return (
    <FloatingShapes
      shapes={shapes}
      color={color}
      speed={speed}
      startFrame={startFrame}
    />
  );
};

/**
 * GeometricPattern - Structured pattern of shapes
 */
export interface GeometricPatternProps {
  /** Pattern type */
  pattern?: 'grid' | 'diagonal' | 'scattered';
  /** Shape type to use */
  shapeType?: ShapeType;
  /** Number of shapes */
  count?: number;
  /** Shape color */
  color?: string;
  /** Start frame */
  startFrame?: number;
}

export const GeometricPattern: React.FC<GeometricPatternProps> = ({
  pattern = 'scattered',
  shapeType = 'hexagon',
  count = 6,
  color = 'rgba(255, 255, 255, 0.08)',
  startFrame = 0,
}) => {
  const random = seededRandom(77777);

  let shapes: FloatingShape[] = [];

  switch (pattern) {
    case 'grid':
      const cols = Math.ceil(Math.sqrt(count));
      const rows = Math.ceil(count / cols);
      for (let i = 0; i < count; i++) {
        const col = i % cols;
        const row = Math.floor(i / cols);
        shapes.push({
          type: shapeType,
          x: 15 + (col / (cols - 1 || 1)) * 70,
          y: 15 + (row / (rows - 1 || 1)) * 70,
          size: 50 + random() * 30,
          rotation: random() * 30,
          blur: 4,
          opacity: 0.06 + random() * 0.06,
        });
      }
      break;

    case 'diagonal':
      for (let i = 0; i < count; i++) {
        const t = i / (count - 1);
        shapes.push({
          type: shapeType,
          x: 10 + t * 80,
          y: 10 + t * 80,
          size: 40 + random() * 40,
          rotation: random() * 45,
          blur: 3 + random() * 4,
          opacity: 0.08 + random() * 0.08,
        });
      }
      break;

    case 'scattered':
    default:
      for (let i = 0; i < count; i++) {
        shapes.push({
          type: shapeType,
          x: random() * 100,
          y: random() * 100,
          size: 35 + random() * 55,
          rotation: random() * 360,
          blur: 2 + random() * 6,
          opacity: 0.06 + random() * 0.1,
        });
      }
      break;
  }

  return (
    <FloatingShapes
      shapes={shapes}
      color={color}
      speed={0.2}
      startFrame={startFrame}
    />
  );
};
